import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';
import url from '../../constants/config';
import { toasterOn } from '../../shared/store/actions';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

export const checklistSlice = createSlice({
  name: 'checklist',
  initialState: {
    items: [],
    adminNote: '',
    loading: false,
    needsTemplateAssignment: false,
    step2Unlocked: false,
    step3Unlocked: false,
    checklistUnreadCount: 0,
    firstUnreadPlayerId: null,
    notifications: [],    // unread — replaced on each bell open
    recentlyRead: [],     // clicked this session — persists across bell opens (max 5)
    assistants: [],
    fullAccessAssistants: [],
    pendingNotesTaskId: null, // signals AdminChecklist to open notes panel for this taskId
  },
  reducers: {
    setItems: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.items = action.payload;
    },
    updateItem: (state, action) => {
      const idx = state.items.findIndex(i => i._id === action.payload._id);
      if (idx !== -1) {
        // eslint-disable-next-line no-param-reassign
        state.items[idx] = action.payload;
      }
    },
    addItem: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.items = [...state.items, action.payload];
    },
    removeItem: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.items = state.items.filter(i => i._id !== action.payload);
    },
    setAdminNote: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.adminNote = action.payload;
    },
    setNeedsTemplateAssignment: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.needsTemplateAssignment = action.payload;
    },
    setStep2Unlocked: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.step2Unlocked = action.payload;
    },
    setStep3Unlocked: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.step3Unlocked = action.payload;
    },
    setLoading: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.loading = action.payload;
    },
    setChecklistUnreadCount: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.checklistUnreadCount = action.payload.count ?? action.payload;
      // eslint-disable-next-line no-param-reassign
      state.firstUnreadPlayerId = action.payload.firstPlayerId ?? null;
    },
    setNotifications: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.notifications = action.payload;
    },
    setAssistants: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.assistants = action.payload;
    },
    setFullAccessAssistants: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.fullAccessAssistants = action.payload;
    },
    setPendingNotesTaskId: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.pendingNotesTaskId = action.payload;
    },
    markNotificationRead: (state, action) => {
      const taskId = action.payload;
      const moved = state.notifications.filter(n => n.taskId === taskId);
      // If nothing to move, don't touch recentlyRead — prevents double-call from
      // AdminChecklist auto-open effect stripping the item back out
      if (moved.length === 0) return;
      // eslint-disable-next-line no-param-reassign
      state.recentlyRead = [
        ...moved.map(n => ({ ...n, read: true })),
        ...state.recentlyRead.filter(n => n.taskId !== taskId),
      ].slice(0, 5);
      // eslint-disable-next-line no-param-reassign
      state.notifications = state.notifications.filter(n => n.taskId !== taskId);
    },
  },
});

export const {
  setItems,
  updateItem,
  addItem,
  removeItem,
  setAdminNote,
  setLoading,
  setNeedsTemplateAssignment,
  setStep2Unlocked,
  setStep3Unlocked,
  setChecklistUnreadCount,
  setNotifications,
  setAssistants,
  setFullAccessAssistants,
  markNotificationRead,
  setPendingNotesTaskId,
} = checklistSlice.actions;


// ─── Admin thunks ─────────────────────────────────────────────────────────────

export const fetchChecklist = playerId => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(`${url}/checklist/${playerId}`, { headers: getAuthHeader() });
    dispatch(setItems(res.data.items));
    dispatch(setNeedsTemplateAssignment(res.data.needsTemplateAssignment === true));
    dispatch(setStep2Unlocked(res.data.step2Unlocked === true));
    dispatch(setStep3Unlocked(res.data.step3Unlocked === true));
    dispatch(setAdminNote(res.data.adminNote?.content || ''));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load checklist', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchAssistants = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin/assistants`, { headers: getAuthHeader() });
    dispatch(setAssistants(res.data.assistants || []));
  } catch (err) {
    // silently fail
  }
};

export const fetchFullAccessAssistants = (playerId) => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin/players/${playerId}/full-access`, { headers: getAuthHeader() });
    dispatch(setFullAccessAssistants(res.data.fullAssistants || []));
  } catch (err) {
    // silently fail
  }
};

export const grantFullAccess = (playerId, assistantEmail) => async dispatch => {
  try {
    await axios.post(
      `${url}/admin/players/${playerId}/assign-full-access`,
      { assistantEmail },
      { headers: getAuthHeader() }
    );
    dispatch(fetchFullAccessAssistants(playerId));
    dispatch(fetchChecklist(playerId));
  } catch (err) {
    // silently fail
  }
};

export const revokeFullAccess = (playerId, assistantEmail) => async dispatch => {
  try {
    await axios.delete(
      `${url}/admin/players/${playerId}/full-access/${encodeURIComponent(assistantEmail)}`,
      { headers: getAuthHeader() }
    );
    dispatch(fetchFullAccessAssistants(playerId));
    dispatch(fetchChecklist(playerId));
  } catch (err) {
    // silently fail
  }
};

export const assignTaskToAssistant = (playerId, taskId, assistantEmail) => async dispatch => {
  // assistantEmail = null means unassign
  try {
    const res = await axios.patch(
      `${url}/checklist/${playerId}/tasks/${taskId}`,
      { assignedToAssistant: assistantEmail },
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to update assistant assignment', type: 'failed' }));
  }
};

export const updateTaskAdmin = (playerId, taskId, fields) => async dispatch => {
  try {
    const res = await axios.patch(
      `${url}/checklist/${playerId}/tasks/${taskId}`,
      fields,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to update task', type: 'failed' }));
  }
};

export const updateTaskStatus = (playerId, taskId, status) =>
  updateTaskAdmin(playerId, taskId, { status });

export const updateTaskLabel = (playerId, taskId, label) =>
  updateTaskAdmin(playerId, taskId, { label });

export const updateTaskTextValue = (playerId, taskId, textValue) =>
  updateTaskAdmin(playerId, taskId, { textValue });

export const addCustomTask = (playerId, step, label, deadline, athleteVisible, priority, taskOwner, tag, requires) => async dispatch => {
  try {
    const body = { step, label };
    if (deadline) body.deadline = deadline;
    if (athleteVisible !== undefined) body.athleteVisible = athleteVisible;
    if (priority) body.priority = priority;
    if (taskOwner) body.taskOwner = taskOwner;
    if (tag) body.tag = tag;
    if (Array.isArray(requires) && requires.length > 0) body.requires = requires;
    const res = await axios.post(
      `${url}/checklist/${playerId}/tasks`,
      body,
      { headers: getAuthHeader() }
    );
    dispatch(addItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to add task', type: 'failed' }));
  }
};

export const deleteCustomTask = (playerId, taskId) => async dispatch => {
  try {
    await axios.delete(
      `${url}/checklist/${playerId}/tasks/${taskId}`,
      { headers: getAuthHeader() }
    );
    dispatch(removeItem(taskId));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to delete task', type: 'failed' }));
  }
};

export const uploadAttachment = (playerId, taskId, file, description) => async dispatch => {
  try {
    const formData = new FormData();
    formData.append('attachment', file);
    if (description) formData.append('description', description);
    const res = await axios.post(
      `${url}/checklist/${playerId}/tasks/${taskId}/attachment`,
      formData,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to upload attachment', type: 'failed' }));
  }
};

export const deleteAttachment = (playerId, taskId, attachmentId) => async dispatch => {
  try {
    const res = await axios.delete(
      `${url}/checklist/${playerId}/tasks/${taskId}/attachments/${attachmentId}`,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to delete attachment', type: 'failed' }));
  }
};

export const fetchAdminNote = playerId => async dispatch => {
  try {
    const res = await axios.get(`${url}/checklist/${playerId}/note`, { headers: getAuthHeader() });
    dispatch(setAdminNote(res.data.adminNote?.content || ''));
  } catch (err) {
    // silently fail
  }
};

export const saveAdminNote = (playerId, content) => async dispatch => {
  try {
    const res = await axios.put(
      `${url}/checklist/${playerId}/note`,
      { content },
      { headers: getAuthHeader() }
    );
    dispatch(setAdminNote(res.data.adminNote?.content || ''));
    dispatch(toasterOn({ message: 'Note saved', type: 'success' }));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to save note', type: 'failed' }));
  }
};

// ─── Player thunks ────────────────────────────────────────────────────────────

export const fetchMyChecklist = () => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(`${url}/checklist/me`, { headers: getAuthHeader() });
    dispatch(setItems(res.data.items));
    dispatch(setNeedsTemplateAssignment(res.data.needsTemplateAssignment === true));
    dispatch(setStep2Unlocked(res.data.step2Unlocked === true));
    dispatch(setStep3Unlocked(res.data.step3Unlocked === true));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load checklist', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const addMyComment = (taskId, text) => async dispatch => {
  try {
    const res = await axios.post(
      `${url}/checklist/me/tasks/${taskId}/comments`,
      { text },
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
    return res.data.item;
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to add comment', type: 'failed' }));
    return null;
  }
};

export const deleteMyComment = (taskId, commentId) => async dispatch => {
  try {
    const res = await axios.delete(
      `${url}/checklist/me/tasks/${taskId}/comments/${commentId}`,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
    return res.data.item;
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to delete comment', type: 'failed' }));
    return null;
  }
};

export const addMyLink = (taskId, linkUrl) => async dispatch => {
  try {
    const res = await axios.post(
      `${url}/checklist/me/tasks/${taskId}/links`,
      { url: linkUrl },
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to add link', type: 'failed' }));
  }
};

export const deleteMyLink = (taskId, linkId) => async dispatch => {
  try {
    const res = await axios.delete(
      `${url}/checklist/me/tasks/${taskId}/links/${linkId}`,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to delete link', type: 'failed' }));
  }
};

export const addAdminComment = (playerId, taskId, text) => async dispatch => {
  try {
    const res = await axios.post(
      `${url}/checklist/${playerId}/tasks/${taskId}/comments`,
      { text },
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
    return res.data.item;
  } catch (err) {
    const message = err?.response?.data?.message || 'Failed to add comment';
    dispatch(toasterOn({ message, type: 'failed' }));
    return null;
  }
};

export const addTaskNote = (playerId, taskId, text) => async dispatch => {
  try {
    const res = await axios.post(
      `${url}/checklist/${playerId}/tasks/${taskId}/notes`,
      { text },
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
    return res.data.item;
  } catch (err) {
    dispatch(toasterOn({ message: 'Nie udało się dodać notatki', type: 'failed' }));
    return null;
  }
};

export const updateTaskNote = (playerId, taskId, noteId, text) => async dispatch => {
  try {
    const res = await axios.put(
      `${url}/checklist/${playerId}/tasks/${taskId}/notes/${noteId}`,
      { text },
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
    return res.data.item;
  } catch (err) {
    dispatch(toasterOn({ message: 'Nie udało się zaktualizować notatki', type: 'failed' }));
    return null;
  }
};

export const deleteTaskNote = (playerId, taskId, noteId) => async dispatch => {
  try {
    const res = await axios.delete(
      `${url}/checklist/${playerId}/tasks/${taskId}/notes/${noteId}`,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
    return res.data.item;
  } catch (err) {
    dispatch(toasterOn({ message: 'Nie udało się usunąć notatki', type: 'failed' }));
    return null;
  }
};

export const deleteAdminComment = (playerId, taskId, commentId) => async dispatch => {
  try {
    const res = await axios.delete(
      `${url}/checklist/${playerId}/tasks/${taskId}/comments/${commentId}`,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
    return res.data.item;
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to delete comment', type: 'failed' }));
    return null;
  }
};

export const addAdminLink = (playerId, taskId, linkUrl) => async dispatch => {
  try {
    const res = await axios.post(
      `${url}/checklist/${playerId}/tasks/${taskId}/links`,
      { url: linkUrl },
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to add link', type: 'failed' }));
  }
};

export const deleteAdminLink = (playerId, taskId, linkId) => async dispatch => {
  try {
    const res = await axios.delete(
      `${url}/checklist/${playerId}/tasks/${taskId}/links/${linkId}`,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to delete link', type: 'failed' }));
  }
};

export const uploadMyAttachment = (taskId, file, description) => async dispatch => {
  try {
    const formData = new FormData();
    formData.append('attachment', file);
    if (description) formData.append('description', description);
    const res = await axios.post(
      `${url}/checklist/me/tasks/${taskId}/attachment`,
      formData,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to upload attachment', type: 'failed' }));
  }
};

export const deleteMyAttachment = (taskId, attachmentId) => async dispatch => {
  try {
    const res = await axios.delete(
      `${url}/checklist/me/tasks/${taskId}/attachments/${attachmentId}`,
      { headers: getAuthHeader() }
    );
    dispatch(updateItem(res.data.item));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to delete attachment', type: 'failed' }));
  }
};

export const fetchChecklistUnreadCount = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/checklist/unread-count`, { headers: getAuthHeader() });
    dispatch(setChecklistUnreadCount({ count: res.data.count || 0, firstPlayerId: res.data.firstPlayerId || null }));
  } catch (err) {
    // silently fail — non-critical
  }
};

export const markChecklistRead = playerId => async dispatch => {
  try {
    const endpoint = playerId
      ? `${url}/checklist/${playerId}/read`
      : `${url}/checklist/me/read`;
    await axios.patch(endpoint, {}, { headers: getAuthHeader() });
    // Re-fetch accurate count — admin may still have unread from other players
    const res = await axios.get(`${url}/checklist/unread-count`, { headers: getAuthHeader() });
    dispatch(setChecklistUnreadCount({ count: res.data.count || 0, firstPlayerId: res.data.firstPlayerId || null }));
  } catch (err) {
    // silently fail
  }
};

/** Player: mark only ONE task's admin comments as read. */
export const markMyTaskRead = taskId => async dispatch => {
  try {
    dispatch(markNotificationRead(taskId));
    const res = await axios.patch(
      `${url}/checklist/me/tasks/${taskId}/read`,
      {},
      { headers: getAuthHeader() }
    );
    if (res.data.item) dispatch(updateItem(res.data.item));
    dispatch(fetchChecklistUnreadCount());
  } catch (err) {
    // silently fail
  }
};

/** Mark only ONE task's athlete comments as read (keeps other tasks' counts intact). */
export const markTaskRead = (playerId, taskId) => async dispatch => {
  try {
    // Immediately move this notification to recentlyRead (local state)
    dispatch(markNotificationRead(taskId));
    // Tell server to mark those comments read
    const res = await axios.patch(
      `${url}/checklist/${playerId}/tasks/${taskId}/read`,
      {},
      { headers: getAuthHeader() }
    );
    if (res.data?.item) dispatch(updateItem(res.data.item));
    if (typeof res.data?.count === 'number') {
      dispatch(setChecklistUnreadCount({ count: res.data.count, firstPlayerId: res.data.firstPlayerId || null }));
    }
    // Re-fetch the accurate count from server (scans actual comments, never drifts)
    dispatch(fetchChecklistUnreadCount());
  } catch (err) {
    // silently fail
  }
};

export const markTaskSeen = (taskId) => async dispatch => {
  try {
    dispatch(markNotificationRead(taskId));
    await axios.patch(
      `${url}/checklist/tasks/${taskId}/seen`,
      {},
      { headers: getAuthHeader() }
    );
    dispatch(fetchChecklistUnreadCount());
  } catch (err) {
    // silently fail
  }
};

export const fetchChecklistNotifications = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/checklist/notifications`, { headers: getAuthHeader() });
    dispatch(setNotifications(res.data.notifications || []));
  } catch (err) {
  }
};

/** Admin/Assistant: mark all internal notes on a task as read (removes internal_note notification). */
export const markTaskNotesRead = (playerId, taskId) => async dispatch => {
  try {
    dispatch(markNotificationRead(taskId));
    await axios.patch(
      `${url}/checklist/${playerId}/tasks/${taskId}/notes/read`,
      {},
      { headers: getAuthHeader() }
    );
    dispatch(fetchChecklistUnreadCount());
  } catch (err) {
    // silently fail
  }
};

export default checklistSlice.reducer;
